import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive';

import { MiniCalendar, MiniClock, SuccessTickIcon } from '@nw/shared/ui';

import {
  CardContainer,
  DateSlotWrapper,
  IconContainer,
  SecondRowWrapper,
  SlotBookingContainer,
  SlotIsBookedForYouText,
  SubHeadingText,
  SuccessHeading,
  TimeSlotIconWrapper,
  TimeSlotText
} from './styledComponents';

interface EmbeddedFormSuccessPageProp {
  slotDate?: string;
  slotTime?: string;
  title?: string;
  description?: string;
  checkAndDoRedirection?: () => void;
}

export const EmbeddedFormSuccessPage = (props: EmbeddedFormSuccessPageProp) => {
  const { t } = useTranslation();

  const { slotDate, slotTime, title, description, checkAndDoRedirection } =
    props;

  const getHoursValue = (timeString: string) => {
    if (timeString.includes(':')) {
      const [time, meridiem] = timeString.split(':');
      const hours = parseInt(time);
      const minutes = meridiem.slice(0, 2).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    const time =
      timeString.length === 4 ? timeString.substring(0, 2) : timeString[0];
    return `${time}:00`;
  };

  const getAMPMValue = (timeString: string): string => {
    if (timeString.includes(':')) {
      return timeString.slice(-2);
    }
    return timeString.length === 4
      ? timeString.substring(2)
      : timeString.substring(1);
  };

  const getDateAndTime = (): { date: string; time: string } => {
    const timeSlots = slotTime.includes(' : ')
      ? slotTime.split(' : ')[1]
      : slotTime;
    const timeString = timeSlots.split(' - ')[0];

    const dateTime = `${format(
      new Date(slotDate),
      'do MMM yyyy'
    )} at ${getHoursValue(timeString)} ${getAMPMValue(timeString)}`;

    const splitted = dateTime ? dateTime.split('at') : '';
    const date = splitted[0] ? splitted[0].trim()?.slice(0, -4) : '';
    const time = splitted[1] ? splitted[1].trim() : '';
    return { date, time };
  };

  const renderSlotBookingContainer = (): React.ReactElement => {
    const { date, time } = getDateAndTime();
    return (
      <SlotBookingContainer data-testid="slotBookingContainer">
        <SlotIsBookedForYouText>
          {t('common.slotBookedAt')}
        </SlotIsBookedForYouText>
        <SecondRowWrapper>
          <DateSlotWrapper>
            <TimeSlotIconWrapper>
              <MiniCalendar width={16} height={16} />
              <TimeSlotText>{date}</TimeSlotText>
            </TimeSlotIconWrapper>
          </DateSlotWrapper>
          <TimeSlotIconWrapper>
            <MiniClock width={16} height={16} />
            <TimeSlotText>{time}</TimeSlotText>
          </TimeSlotIconWrapper>
        </SecondRowWrapper>
      </SlotBookingContainer>
    );
  };

  const renderIconContainer = (): React.ReactElement => {
    return (
      <IconContainer data-testid="successIcon">
        <MediaQuery maxWidth={768}>
          <SuccessTickIcon height={48} width={48} />
        </MediaQuery>
        <MediaQuery minWidth={769}>
          <SuccessTickIcon height={74} width={74} />
        </MediaQuery>
      </IconContainer>
    );
  };

  return (
    <>
      {checkAndDoRedirection && checkAndDoRedirection()}
      <CardContainer
        data-testid="successCard"
        id="successCard"
        className="successCardContainer"
      >
        {renderIconContainer()}
        <SuccessHeading
          data-testid="successHeading"
          className="success-card-heading"
          dangerouslySetInnerHTML={{
            __html: title
          }}
        />
        <SubHeadingText
          data-testid="description"
          className="success-card-sub-heading"
          dangerouslySetInnerHTML={{
            __html: description
          }}
        />
        {slotDate && slotTime && renderSlotBookingContainer()}
      </CardContainer>
    </>
  );
};
